// AIドラフト検証ケースの「期待の形」（API の expectation。キーはキャメルケース）と、カバレッジ（coverage）の型と表示名。
// 保存形式・検証はサーバ（DunningEvaluationExpectation / DunningEvaluationCoverage）が正で、画面は表示と編集（お手本の文の欄と JSON）だけを持つ
import { isUnsetTerminal } from "../terminals"

/** 判定方法。contains_all：すべて含む／contains_any：どれかを含む／payment_guidance：■お支払案内がある／llm：AI による送れるか判定だけが見る */
export type ExpectationCheck = {
  type: "contains_all" | "contains_any" | "payment_guidance" | "llm"
  values?: string[]
  note?: string
}

export type ExpectationItem = { id: string; label?: string; check: ExpectationCheck }

/** 模範文。approved（正解）は Ops が確認したものだけ。reference（参考）は言い回しの参考で正解ではない */
export type Exemplar = {
  body: string
  status?: "reference" | "approved"
  source?: "operator_sent" | "curated" | "ai_original"
  note?: string
}

export type Expectation = {
  /** 許す対応。先頭は期待する対応（expectedTerminal）と同じ */
  strategy?: { terminals?: string[] }
  required?: ExpectationItem[]
  forbidden?: ExpectationItem[]
  /** 本文（支払案内を除く）の文字数の上限 */
  maxLength?: number
  tone?: "firm" | "neutral" | "caring"
  exemplars?: Exemplar[]
  /** このケースでは効かせない本文ルールの ID */
  ruleExemptions?: string[]
}

export type Coverage = { difficulty?: "typical" | "boundary" | "composite"; tags?: string[] }

export const TONE_LABELS: Record<NonNullable<Expectation["tone"]>, string> = {
  firm: "毅然",
  neutral: "通常",
  caring: "配慮",
}

export const EXEMPLAR_STATUS_LABELS: Record<NonNullable<Exemplar["status"]>, string> = {
  reference: "参考",
  approved: "正解",
}

export const EXEMPLAR_SOURCE_LABELS: Record<NonNullable<Exemplar["source"]>, string> = {
  operator_sent: "オペ送信文",
  curated: "作成",
  ai_original: "AI原文（直される前）",
}

export const DIFFICULTY_LABELS: Record<NonNullable<Coverage["difficulty"]>, string> = {
  typical: "典型",
  boundary: "境界",
  composite: "複合",
}

/** 判定方法の短い説明（例「「9月20日」「30,000円」をすべて含む」） */
export const checkLabel = (check: ExpectationCheck) => {
  const quoted = (check.values ?? []).map((value) => `「${value}」`).join("")
  switch (check.type) {
    case "contains_all":
      return `${quoted}をすべて含む`
    case "contains_any":
      return `${quoted}のどれか`
    case "payment_guidance":
      return "■お支払案内"
    default:
      return check.note ? `AI による送れるか判定：${check.note}` : "AI による送れるか判定"
  }
}

/**
 * 期待する対応を変えたら、許す対応の先頭（前の期待する対応）を差し替える。
 * 未定は許す対応と組み合わせられない（API で弾く）ので、未定にしたときは許す対応を外す
 */
export const retargetExpectation = (
  expectation: Expectation | null | undefined,
  from: string,
  to: string
): Expectation | null | undefined => {
  if (!expectation?.strategy?.terminals) return expectation
  const rest = isUnsetTerminal(to)
    ? []
    : expectation.strategy.terminals.filter((id) => id !== from && id !== to)
  return { ...expectation, strategy: { ...expectation.strategy, terminals: [to, ...rest] } }
}

/** 編集欄に出す JSON（未設定は期待する対応だけの期待） */
export const expectationJson = (expectation: Expectation | null | undefined, terminal: string) =>
  JSON.stringify(expectation ?? { strategy: { terminals: terminal ? [terminal] : [] } }, null, 2)

/** 編集欄の JSON を読む。オブジェクトでなければエラーの文言を返す */
export const parseExpectationJson = (
  text: string
): { value: Expectation; error: null } | { value: null; error: string } => {
  try {
    const value = JSON.parse(text)
    if (value && typeof value === "object" && !Array.isArray(value)) {
      return { value: value as Expectation, error: null }
    }
    return { value: null, error: "期待の形は JSON のオブジェクト（{ … }）で書いてください" }
  } catch {
    return { value: null, error: "JSON として読めません（この欄の変更はまだ反映していません）" }
  }
}

/** ai_original は当時の AI の下書き（オペレーターが直す前の文）で、お手本ではない */
const isAiOriginal = (exemplar: Exemplar) => exemplar.source === "ai_original"

export const modelExemplars = (expectation: Expectation | null | undefined) =>
  (expectation?.exemplars ?? []).filter((exemplar) => !isAiOriginal(exemplar))

export const aiOriginalExemplars = (expectation: Expectation | null | undefined) =>
  (expectation?.exemplars ?? []).filter(isAiOriginal)

const CURATED: Pick<Exemplar, "status" | "source"> = { status: "reference", source: "curated" }

export const withModelExemplar = (
  expectation: Expectation | null | undefined,
  body: string,
  terminal: string,
  fresh?: Pick<Exemplar, "status" | "source">
): Expectation => {
  const base: Expectation = expectation ?? { strategy: { terminals: terminal ? [terminal] : [] } }
  const current = base.exemplars ?? []
  const index = current.findIndex((exemplar) => !isAiOriginal(exemplar))
  const exemplars =
    body.trim() === ""
      ? current.filter((_, i) => i !== index)
      : index >= 0
        ? current.map((exemplar, i) =>
            i === index ? (fresh ? { body, ...fresh } : { ...exemplar, body }) : exemplar
          )
        : [{ body, ...(fresh ?? CURATED) }, ...current]
  const rest = withoutExemplars(base)
  return exemplars.length ? { ...rest, exemplars } : rest
}

export const withoutExemplars = (expectation: Expectation): Expectation =>
  Object.fromEntries(
    Object.entries(expectation).filter(([key]) => key !== "exemplars")
  ) as Expectation
